import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../../config/URL";
import { exportVisibleColumnsToCSV } from "../../../components/common/ExportCsv";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";

const MODULE_KEY = "WIDTHDRAWLIST";

const StudentWithdraw = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    studentName: "",
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const canRead = Boolean(storedScreens?.withdrawRead);
  const canUpdate = Boolean(storedScreens?.withdrawUpdate);
  const defaultColumnVisibility = {
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 110,
        Cell: ({ row }) => {
          const status = row.original.status || "PENDING";

          let badgeColor = "secondary";
          if (status === "APPROVED") badgeColor = "success";
          if (status === "CANCEL") badgeColor = "danger";
          if (status === "PENDING" || status === "SUBMITTED")
            badgeColor = "warning";

          const isFinal = ["APPROVED", "CANCEL"].includes(status);

          return isFinal ? (
            <span className={`badge bg-${badgeColor} fw-light px-2 py-1`}>
              {status}
            </span>
          ) : (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
                setSelectedRow(row.original);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "centerName",
        header: "Centre",
      },
      {
        accessorKey: "studentUniqueId",
        header: "Student ID",
      },
      {
        accessorKey: "studentName",
        header: "Student Name",
      },
      {
        accessorKey: "courseName",
        header: "Course",
      },
      {
        accessorKey: "className",
        header: "Class",
      },
      {
        accessorKey: "lastLessonDate",
        header: "Last Lesson",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "noOfLessonToRefund",
        header: "Lessons Refund",
        size: 120,
      },
      {
        accessorKey: "totalRefundableAmount",
        header: "Refund Amount",
        Cell: ({ cell }) => {
          const val = cell.getValue();
          return val ? `$${val.toFixed(2)}` : "-";
        },
      },
      {
        accessorKey: "refundMode",
        header: "Refund Mode",
        Cell: ({ cell }) => {
          const mode = cell.getValue();
          return mode?.replace(/_/g, " ") || "-";
        },
      },
      {
        accessorKey: "withdrawReason",
        header: "Reason",
      },
      {
        accessorKey: "requestDate",
        header: "Request Date",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "-",
      },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "-",
      },
    ],
    [],
  );

  /* -------------------- API -------------------- */
  const fetchApiData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("centerId", filters.centerId || "0");
      if (filters.studentName) {
        queryParams.append("studentName", filters.studentName);
      }

      const response = await api.get(
        `/getStudentWithdrawClass?methodStatus=WITHDRAWN&${queryParams.toString()}`,
      );
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
  }, [selectedCenter]);

  useEffect(() => {
    fetchApiData();
  }, [filters]);

  const handleStatusChange = async (newStatus) => {
    if (!selectedRow) return;

    try {
      const response = await api.put(
        `/updateWithdrawClassStatus/${selectedRow.id}?endWithdrawStatus=${newStatus}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.status === 200) {
        setData((prev) =>
          prev.map((item) =>
            item.id === selectedRow.id ? { ...item, status: newStatus } : item,
          ),
        );
        toast.success(
          `Student change class status updated to ${newStatus} successfully.`,
        );
        handleMenuClose();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "An error occurred while updating status.",
      );
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilter = () => {
    setFilters({
      centerId: selectedCenter,
      studentName: "",
    });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {},
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "StudentWithdrawList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: canRead
      ? ({ row }) => ({
          onClick: (e) => {
            if (e.target.closest(".MuiIconButton-root")) return;
            navigate(`/withdraw/view/${row.original.id}`);
          },
          style: { cursor: "pointer" },
        })
      : undefined,
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": { color: "#eb862a" },
          },
          track: { backgroundColor: "#e0e0e0" },
          thumb: { color: "#eb862a" },
          switchBase: {
            "&.Mui-checked": { color: "#eb862a" },
            "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#eb862a" },
          },
        },
      },
    },
  });

  return (
    <div className="container-fluid px-2 my-4 center">
      <ol className="breadcrumb my-3" style={{ listStyle: "none", margin: 0 }}>
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Student Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">Withdraw List</li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Withdraw List</strong>
          </span>
        </div>

        <div className="mb-3 d-flex">
          <input
            type="text"
            name="studentName"
            value={filters.studentName}
            onChange={handleFilterChange}
            className="form-control form-control-sm center_list ms-2"
            style={{ width: "160px" }}
            placeholder="Student Name"
            autoComplete="off"
          />
          <button
            type="button"
            onClick={clearFilter}
            className="btn btn-sm btn-border ms-2"
          >
            Clear
          </button>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>
            {canUpdate && (
              <Menu
                id="action-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => handleStatusChange("APPROVED")}
                  className="text-start mb-0 menuitem-style"
                >
                  Approve
                </MenuItem>
                <MenuItem
                  onClick={() => handleStatusChange("CANCEL")}
                  className="text-start mb-0 menuitem-style"
                >
                  Cancel
                </MenuItem>
              </Menu>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentWithdraw;
